import React, { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { callBanner, TargetParams, WindowWithAdriver } from './adriver';
import { AdriverBannerId } from './Banners.constants';
import { BannerName } from './Banners.types';
import { adjustBackgroundBannerOffset } from './Banners.helpers';
import { handleAndLoadBanner } from './AdriverBanner.helpers';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';
import {
  BannerSource,
  SetBannerSource,
} from '@/store/useBannerStore/useBannerStore.types';
import { sleep } from '@/utils/helpers';

interface BannerProps {
  containerId: string;
  containerClassName?: string;
  tagName: string;
  sid: string;
  bn: string;
  bt?: string;
  width?: string;
  height?: string;
  arCn?: number;
  targetParams?: TargetParams;
  isAjax?: boolean;
}

export const AdriverBanner: React.FC<BannerProps> = ({
  containerId,
  containerClassName,
  tagName,
  sid,
  bn,
  bt = '43',
  width,
  height,
  arCn,
  targetParams,
  isAjax,
}) => {
  const pathname = usePathname();
  const tag = tagName || 'div';
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [
    isBlockerDetected,
    bannerTopSource,
    bannerBottomSource,
    bannerBackgroundSource,
    bannerCatfishSource,
    setBannerTopSource,
    setBannerBottomSource,
    setBannerBackgroundSource,
    setBannerCatfishSource,
    setIsAdriverTopBannerVisible,
  ] = useBannerStore(state => [
    state.isBlockerDetected,
    state.bannerTopSource,
    state.bannerBottomSource,
    state.bannerBackgroundSource,
    state.bannerCatfishSource,
    state.setBannerTopSource,
    state.setBannerBottomSource,
    state.setBannerBackgroundSource,
    state.setBannerCatfishSource,
    state.setIsAdriverTopBannerVisible,
  ]);

  function handleBannerLoadState(
    bannerType: keyof typeof AdriverBannerId,
    loaded: boolean
  ) {
    if (bannerType === BannerName.Top) {
      setIsAdriverTopBannerVisible(loaded);
      adjustBackgroundBannerOffset();
    }
  }

  async function checkBanner(
    selector: string,
    setBannerSource: SetBannerSource,
    signal: AbortSignal
  ) {
    await sleep(5_000);

    if (signal.aborted) return;

    const banner = document.querySelector(selector);
    const isBannerLoaded = !!banner?.clientHeight && banner.clientHeight > 0;

    if (!isBannerLoaded) {
      setBannerSource(BannerSource.Admanager);
    }
  }

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const bannerElement = document.createElement(tag);

    bannerElement.id = containerId;

    if (containerClassName) {
      bannerElement.className = containerClassName;
    }

    wrapper?.appendChild(bannerElement);

    if (!isBlockerDetected) {
      callBanner({
        container_id: containerId,
        sid,
        bn,
        bt,
        width,
        height,
        arCn,
        target: targetParams,
        isAjax,
      });
    } else {
      typeof (window as WindowWithAdriver).adriverExtentionLoad !==
        'undefined' &&
        (window as WindowWithAdriver).adriverExtentionLoad?.(
          0,
          null,
          containerId
        );
    }

    return () => {
      if (bannerElement.parentNode === wrapper) {
        wrapper?.removeChild(bannerElement);
      }
    };
  }, [pathname]);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    if (containerId === AdriverBannerId[BannerName.Top]) {
      setIsAdriverTopBannerVisible(false);
      adjustBackgroundBannerOffset();

      handleAndLoadBanner(
        BannerName.Top,
        setBannerTopSource,
        bannerTopSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-top-banner', setBannerTopSource, signal);
    }

    if (containerId === AdriverBannerId[BannerName.Bottom]) {
      handleAndLoadBanner(
        BannerName.Bottom,
        setBannerBottomSource,
        bannerBottomSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-bottom-banner', setBannerBottomSource, signal);
    }

    if (containerId === AdriverBannerId[BannerName.Background]) {
      handleAndLoadBanner(
        BannerName.Background,
        setBannerBackgroundSource,
        bannerBackgroundSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-bg-left', setBannerBackgroundSource, signal);
    }

    if (containerId === AdriverBannerId[BannerName.Catfish]) {
      handleAndLoadBanner(
        BannerName.Catfish,
        setBannerCatfishSource,
        bannerCatfishSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner(
        `#${AdriverBannerId[BannerName.Catfish]}`,
        setBannerCatfishSource,
        signal
      );
    }

    return () => {
      abortController.abort();
    };
  }, [pathname]);

  return <div ref={wrapperRef} className={containerClassName} />;
};
